import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { Spin } from "antd";
import * as _ from "lodash";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

import { useMachine } from "@/_zustand/machine";
import { useProductStore } from "@/_zustand/store";
import { useOrder } from "@/_zustand/order";
import { useAuth } from "@/_zustand/auth";
import {
  useConfirmTransaction,
  useReverseTransaction,
  useCreateOrder,
} from "@/hooks/useTransbank";
import { useValidStockBolsa } from "@/hooks/useProductos";
import { mapCartForOrder, buildAppliedDiscount } from "@/lib";
import { useTransbankCredenciales } from "@/hooks/useTransbankCredenciales";
import {
  adquirirCandadoPago,
  liberarCandadoPago,
  esTokenVigente,
  reclamarIntento,
  debeReportarBloqueo,
  bloqueosAcumulados,
} from "@/lib/paymentGuard";
import {
  filtrarCredencialesParaBody,
  leerCredencialesIntento,
  limpiarCredencialesIntento,
} from "@/lib/transbankCredenciales";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const WebPay = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  let query = useQuery();
  const { setOrder, order } = useOrder();
  const { machineSelected, addressMachine, machine } = useMachine();
  const {
    calculateTotals,
    total,
    products,
    buyOrder,
    setFinishOrder,
    descuento_id,
    descuento_data,
  } = useProductStore();
  const confirmTransaction = useConfirmTransaction();
  const reverseTransaction = useReverseTransaction();
  const createOrder = useCreateOrder();
  const { user, incrementTotalPedidos } = useAuth();
  const validStockBolsa = useValidStockBolsa();
  const totalPedido = useRef(total);
  const confirmTransactionResp = useRef([]);
  const reversaLanzada = useRef(false);
  // Token de ownership del candado (ver paymentGuard.js): compartido entre
  // el efecto de montaje y sendCreateTransaccion/validaStock/sendPedido2/
  // reverseWebPayTransaction, funciones separadas del componente.
  const candadoTokenRef = useRef(null);
  const credencialesEnVivo = useTransbankCredenciales("webpay");
  // Credenciales del intento (AC-8/AC-9/AC-10): re-resueltas desde el
  // snapshot guardado por WebPayButton.jsx bajo "wp:"+token, para sobrevivir
  // al remount de /pay-detail tras el redirect de Webpay y no depender de
  // `machine` "fresco" (borde 3). Si el snapshot no existe, cae a la
  // resolución en vivo.
  const credencialesIntentoRef = useRef(null);

  useEffect(() => {
    calculateTotals(machineSelected);

    const tokenWs = query.get("token_ws");

    if (tokenWs) {
      // Idempotencia del retorno de Webpay (AC 15/16, equivalente directo de
      // claimCheckoutReturn("wp:"+token_ws) en Sima-appmovil): un remount,
      // navegación hacia atrás/adelante o doble evento para el MISMO
      // token_ws no debe volver a confirmar/crear el pedido.
      if (!reclamarIntento("wp:" + tokenWs)) {
        if (debeReportarBloqueo("wp:" + tokenWs)) {
          Sentry.captureMessage(
            "Segundo intento de pago bloqueado por candado",
            {
              level: "warning",
              tags: { payment_flow: "webpay" },
              extra: {
                buyOrder,
                motivo: "retorno_ya_procesado",
                tokenLen: tokenWs.length,
                bloqueos: bloqueosAcumulados("wp:" + tokenWs),
              },
            },
          );
        }
        if (order && order.pedido_id) {
          return navigate(getCompanyRoute("order"), { replace: true });
        }
        return navigate(getCompanyRoute("cart"), { replace: true });
      }

      // Candado en vuelo (protege incluso sin sessionStorage disponible: el
      // efecto corriendo dos veces en la misma vida de página).
      const candado = adquirirCandadoPago("webpay");
      if (!candado) {
        return navigate(getCompanyRoute("cart"), { replace: true });
      }
      candadoTokenRef.current = candado;

      // AC-8: mismas credenciales usadas en el getFormTransaction que
      // generó este token_ws. Si el snapshot expiró/storage bloqueado, cae
      // a la resolución en vivo y reporta un warning (borde 2/4).
      let cred = leerCredencialesIntento("wp:" + tokenWs);
      if (!cred) {
        cred = credencialesEnVivo;
        Sentry.captureMessage(
          "Snapshot de credenciales Webpay no encontrado, usando resolución en vivo",
          {
            level: "warning",
            tags: { payment_flow: "webpay" },
            extra: { maquina_id: machineSelected },
          },
        );
      }
      credencialesIntentoRef.current = cred;

      let formData = {
        token: tokenWs,
        maquina_id: machineSelected,
        ...filtrarCredencialesParaBody({
          codeComerce: cred.codeComerce,
          apiKey: cred.apiKey,
        }),
      };

      confirmTransaction
        .mutateAsync(formData)
        .then((resp) => {
          if (resp.status === "AUTHORIZED") {
            // Segunda capa de ownership (ver paymentGuard.js): esta
            // respuesta puede ser la llegada tardía de un intento cuyo
            // candado ya expiró por TTL y fue re-adquirido por un reintento
            // más nuevo. Si ya no es el dueño vigente, se descarta
            // silenciosamente esta rama en vez de seguir a
            // sendCreateTransaccion/validaStock/sendPedido2/createOrder con
            // datos obsoletos (evita un doble pedido/cobro).
            if (!esTokenVigente("webpay", candadoTokenRef.current)) {
              Sentry.captureMessage(
                "Respuesta tardía de un intento de pago obsoleto descartada",
                {
                  level: "warning",
                  tags: { payment_flow: "webpay" },
                  extra: {
                    buyOrder,
                    maquina_id: machineSelected,
                    motivo: "token_candado_ya_no_vigente",
                  },
                },
              );
              return;
            }
            confirmTransactionResp.current = resp;
            totalPedido.current = resp.amount;
            sendCreateTransaccion(resp);
          } else {
            // Misma segunda capa de ownership que la rama de éxito: un
            // rechazo tardío de un intento obsoleto (candado ya
            // re-adquirido por un reintento más nuevo) no debe expulsar al
            // usuario al carrito con un mensaje falso, después de que su
            // compra ya se completó con el intento nuevo.
            if (!esTokenVigente("webpay", candadoTokenRef.current)) {
              Sentry.captureMessage(
                "Respuesta tardía de un intento de pago obsoleto descartada",
                {
                  level: "warning",
                  tags: { payment_flow: "webpay" },
                  extra: {
                    buyOrder,
                    maquina_id: machineSelected,
                    motivo: "token_candado_ya_no_vigente",
                  },
                },
              );
              return;
            }
            liberarCandadoPago("webpay", candadoTokenRef.current);
            limpiarCredencialesIntento("wp:" + tokenWs);
            toast.error("El pago fue rechazado por el banco");
            return navigate(getCompanyRoute("cart"), { replace: true });
          }
        })
        .catch(() => {
          // Misma segunda capa de ownership: el error de red de un intento
          // obsoleto (candado ya re-adquirido por un reintento más nuevo)
          // no debe expulsar al usuario al carrito.
          if (!esTokenVigente("webpay", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "webpay" },
                extra: {
                  buyOrder,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          liberarCandadoPago("webpay", candadoTokenRef.current);
          toast.error("Error al verificar la transacción");
          return navigate(getCompanyRoute("cart"), { replace: true });
        });
    }
    if (query.get("TBK_TOKEN")) {
      toast.error("El pago fue rechazado por el usuario");
      return navigate(getCompanyRoute("cart"), { replace: true });
    }
    // Este efecto debe ejecutarse una sola vez al montar, para procesar los
    // query params (token_ws/TBK_TOKEN) del redirect de Webpay. Agregar las
    // dependencias sugeridas re-dispararía la verificación de la transacción
    // en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendCreateTransaccion = (data) => {
    // Incidencia #10: se eliminó la llamada directa a createTransaccion porque
    // createOrder (llamado en sendPedido2) ya registra la transacción en el backend.
    // Llamar a ambos producía 2 filas en la tabla transacciones.
    confirmTransactionResp.current = data;
    totalPedido.current = data.amount;
    validaStock();
  };

  const validaStock = () => {
    let productsFiltered = _.filter(
      products,
      (item) => item.maquina_id == machineSelected,
    );
    let productsMap = _.map(productsFiltered, (item) => {
      return {
        ...item,
        inventario_slot: item.inventario_slot,
        maquina_id: item.maquina_id,
        producto_cant: item.producto_cant,
        producto_descripcion: item.producto_descripcion,
        producto_formato: `${item.cantidad} ${item.presentacion_nombre}`,
        producto_id: item.producto_id,
        producto_nombre: item.producto_nombre,
        producto_precio: item.producto_precio,
        producto_oferta: item.producto_oferta,
        cantidad: item.producto_cant,
        logo_empresa: item.logo_machine,
        distancia: "13.18",
        latitud: machine.maquina_latitud,
        longitud: machine.maquina_longitud,
        full_count: "161",
        maquina_direccion: machine.maquina_direccion,
        maquina_nombre: machine.maquina_nombre,
        empresa_id: machine.empresa_id,
      };
    });

    let formData = {
      dataBolsa: productsMap,
    };

    validStockBolsa
      .mutateAsync(formData)
      .then((resp) => {
        if (resp.status == "success" && resp.stock) {
          sendPedido2();
        } else {
          reverseWebPayTransaction();
          toast.error("No hay stock suficiente para realizar la compra");
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error al validar el stock");
        reverseWebPayTransaction();
      });
  };

  const sendPedido2 = () => {
    let dataSend = {
      appPlatform: "web",
      userId: user.usuario_id,
      cart: mapCartForOrder(products, machineSelected),
      storeId: machineSelected,
      total: totalPedido.current,
      buyOrder: buyOrder,
      appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),

      transactionDetail: {
        // Incidencia #1: agregar buy_order al nivel superior de transactionDetail
        // para que el backend lo guarde correctamente en la tabla transacciones.
        buy_order: buyOrder,
        details: [
          {
            amount: confirmTransactionResp.current.amount,
            status: confirmTransactionResp.current.status,
            authorization_code:
              confirmTransactionResp.current.authorization_code,
            payment_type_code: confirmTransactionResp.current.payment_type_code,
            response_code: confirmTransactionResp.current.response_code,
            installments_number:
              confirmTransactionResp.current.installments_number,
            // AC-9: mismo codeComerce efectivo usado en confirmTransaction
            // (antes, en dev/QA, este código era el de OneClick, no el de
            // Webpay — VITE_CODE_COMERCE_DEV en lugar de
            // VITE_CODE_COMERCE_WEBPAY_DEV).
            commerce_code:
              credencialesIntentoRef.current?.codeComerce ||
              credencialesEnVivo.codeComerce ||
              "",
            buy_order: buyOrder,
            prepaid_balance: 0,
          },
        ],
        card_detail: {
          card_number: confirmTransactionResp.current.card_detail.card_number,
        },
        accounting_date: confirmTransactionResp.current.accounting_date,
        transaction_date: confirmTransactionResp.current.transaction_date,
      },
    };

    createOrder
      .mutateAsync(dataSend)
      .then((resp) => {
        //MANEJAR ERROR
        if (resp.status === "success") {
          // Camino de éxito (AC 6): el candado NO se libera; la navegación a
          // /order es el único desenlace posible desde aquí.
          limpiarCredencialesIntento("wp:" + query.get("token_ws"));
          setOrder({
            ...resp,
            pedido_total: totalPedido.current,
            maquina_direccion: addressMachine,
            maquina_telefono: machine.maquina_telefono,
            usuario_correo: user.usuario_correo,
          });
          setFinishOrder(machineSelected);
          toast.success("EXITO");
          incrementTotalPedidos();
          return navigate(getCompanyRoute("order"), { replace: true });
        } else {
          liberarCandadoPago("webpay", candadoTokenRef.current);
          toast.error(resp.message);
          return navigate(getCompanyRoute("cart"), { replace: true });
        }
      })
      .catch(() => {
        toast.error("Error al realizar el pedido");
        reverseWebPayTransaction();
        //MANEJAR ERROR
      });
  };

  const reverseWebPayTransaction = () => {
    // AC 10 (equivalente Webpay): la reversa se ejecuta una sola vez,
    // aunque validaStock y sendPedido2 puedan disparar este camino.
    if (reversaLanzada.current) return;
    reversaLanzada.current = true;

    // AC-10: mismas credenciales que confirmTransaction/getFormTransaction
    // de esta transacción, nunca re-resueltas desde `machine` (borde 3).
    const cred = credencialesIntentoRef.current ?? credencialesEnVivo;

    let formData = {
      token: query.get("token_ws"),
      maquina_id: machineSelected,
      amount: total,
      ...filtrarCredencialesParaBody({
        codeComerce: cred.codeComerce,
        apiKey: cred.apiKey,
      }),
    };
    reverseTransaction
      .mutateAsync(formData)
      .then(() => {
        liberarCandadoPago("webpay", candadoTokenRef.current);
        limpiarCredencialesIntento("wp:" + query.get("token_ws"));
        return navigate(getCompanyRoute("cart"), { replace: true });
      })
      .catch((error) => {
        // Riesgo financiero: el cobro pudo haber quedado hecho y la reversa
        // (el intento de anularlo) falló — necesita revisión manual.
        Sentry.captureException(error, {
          level: "error",
          tags: { financial_risk: true, payment_flow: "webpay" },
          extra: { buyOrder, maquina_id: machineSelected },
        });
        liberarCandadoPago("webpay", candadoTokenRef.current);
        toast.error("Error al reversar la transacción");
      });
  };

  // {
  //   "appPlatform": "APP-ios-2.3.0317",
  //   "userId": 339,
  //   "cart": [
  //     {
  //       "maquina_id": 132,
  //       "producto_id": 441,
  //       "stock": 15,
  //       "stock_maximo": 50,
  //       "inventario_slot": 100,
  //       "producto_descripcion": "Agua Mineral Sin Gas Botella",
  //       "producto_precio": 730,
  //       "producto_foto": "https://...",
  //       "producto_nombre": "Agua Mineral Sin Gas Botella",
  //       "estado_id": 1,
  //       "estado_nombre": "Activo",
  //       "cantidad": 2,
  //       "presentacion_id": 4,
  //       "presentacion_nombre": "ml",
  //       "descuento_dosxuno": 0,
  //       "descuento_porcentaje": 0,
  //       "descuento_precio_final": 730,
  //       "descuento_tipo_id": null,
  //       "descuento_valor": null,
  //       "descuento_id": null,
  //       "producto_oferta": 0,
  //       "full_count": "17",
  //       "maquina_nombre": "Maq. Estado",
  //       "maquina_direccion": "Nestle (Estado #420)",
  //       "logo_empresa": "https://...",
  //       "empresa_id": 9,
  //       "latitud": -33.4380640086957,
  //       "longitud": -70.6495895043478,
  //       "distancia": "10856.71",
  //       "vende_licor": 1
  //     }
  //   ],
  //   "storeId": "132",
  //   "total": null,
  //   "appliedDiscount": {
  //     "tipo": "total_monto",
  //     "valor": 2000,
  //     "descuento_id": 269,
  //     "descuento_nombre": "NTMontoFijoTotal",
  //     "simbolo": "$"
  //   },
  //   "transactionDetail": {
  //     "details": [
  //       {
  //         "amount": 1460,
  //         "status": "AUTHORIZED",
  //         "authorization_code": "1213",
  //         "payment_type_code": "VN",
  //         "response_code": 0,
  //         "installments_number": 0,
  //         "commerce_code": "597055555542",
  //         "buy_order": "Mall-678009849"
  //       }
  //     ],
  //     "buy_order": "Mall-678009849",
  //     "card_detail": { "card_number": "6623" },
  //     "accounting_date": "0724",
  //     "transaction_date": "2025-07-24T18:48:16.716Z"
  //   }
  // }

  return (
    <div className="row">
      <div className="col-12 mb-3 inter-regular text-center">
        <h3 className="text-black inter-semibold ">
          Validando la compra, por favor espere!
        </h3>
        <Spin tip="Verificando..." size="large">
          <div style={{ padding: 50, borderRadius: 4 }} />
        </Spin>
      </div>
    </div>
  );
};
